import React from 'react';
import { Box, Typography, Grid, Paper, CircularProgress, Alert } from '@mui/material';
import { Phone, Email, LocationOn, AccessTime } from '@mui/icons-material';
import MainLayout from '../layouts/MainLayout';
import { usePrimaryLocation } from '../hooks/usePrimaryLocation';

// Utility function to format phone number
const formatPhoneForDisplay = (phone?: string): string => {
  if (!phone) return 'Phone number not available';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

// Utility function to format address
const formatFullAddress = (location: any): string => {
  if (!location) return 'Address not available';
  
  const addressParts = [];
  if (location.address) addressParts.push(location.address);
  if (location.city) addressParts.push(location.city);
  if (location.state) addressParts.push(location.state);
  if (location.zipCode) addressParts.push(location.zipCode);
  
  return addressParts.length > 0 ? addressParts.join(', ') : 'Address not available';
};

// Utility function to format operating hours
const formatOperatingHours = (hours: any): JSX.Element[] => {
  if (!hours) return [<Typography key="no-hours">Operating hours not available</Typography>];
  
  const daysOfWeek = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];
  
  return daysOfWeek.map(day => {
    const dayHours = hours[day.key];
    const hoursText = dayHours ? `${dayHours.open} - ${dayHours.close}` : 'Closed';
    
    return (
      <Typography key={day.key} variant="body2" sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <span>{day.label}:</span>
        <span>{hoursText}</span>
      </Typography>
    );
  });
};

const ContactPage: React.FC = () => {
  const { primaryLocation, loading, error } = usePrimaryLocation();

  return (
    <MainLayout>
      <div className="page-banner">
        <div className="container">
          <h1>Contact Us</h1>
          <p>We'd love to hear from you</p>
        </div>
      </div>

      <section className="contact-section" style={{ padding: '4rem 0' }}>
        <div className="container">
          {loading && (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {primaryLocation && (
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h5" gutterBottom color="primary">
                    {primaryLocation.name}
                  </Typography>
                  
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <LocationOn color="primary" sx={{ mr: 2, mt: 0.5 }} />
                      <Box>
                        <Typography variant="h6" gutterBottom>Address</Typography>
                        <Typography variant="body1" color="text.secondary">
                          {formatFullAddress(primaryLocation)}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Phone color="primary" sx={{ mr: 2 }} />
                      <Box>
                        <Typography variant="h6" gutterBottom>Phone</Typography>
                        <Typography variant="body1" color="text.secondary">
                          {formatPhoneForDisplay(primaryLocation.phone)}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Email color="primary" sx={{ mr: 2 }} />
                      <Box>
                        <Typography variant="h6" gutterBottom>Email</Typography>
                        <Typography variant="body1" color="text.secondary">
                          {primaryLocation.email || 'Email not available'}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {primaryLocation.website && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="h6" gutterBottom>Website</Typography>
                        <Typography variant="body1" color="primary">
                          <a href={primaryLocation.website} target="_blank" rel="noopener noreferrer">
                            {primaryLocation.website}
                          </a>
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h5" gutterBottom color="primary">
                    <AccessTime sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Operating Hours
                  </Typography>
                  
                  <Box sx={{ mt: 3 }}>
                    {formatOperatingHours(primaryLocation.operatingHours)}
                  </Box>
                  
                  {primaryLocation.description && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" gutterBottom>About This Location</Typography>
                      <Typography variant="body1" color="text.secondary">
                        {primaryLocation.description}
                      </Typography>
                    </Box>
                  )}
                  
                  {primaryLocation.manager && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" gutterBottom>Manager</Typography>
                      <Typography variant="body1" color="text.secondary">
                        {primaryLocation.manager}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
          
          {!loading && !primaryLocation && (
            <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                Contact information is currently being updated. Please check back soon.
              </Typography>
            </Paper>
          )}
        </div>
      </section>
    </MainLayout>
  );
};

export default ContactPage; 